import _get from 'lodash/get'
import React, { useMemo } from 'react'
import { useController, useFormContext } from 'react-hook-form'
import { P, match } from 'ts-pattern'

import { useFlowsServicesResponses } from '@flows-ui/services/FlowServicesResponsesContext'
import { useTranslation } from '@flows-ui/utils/i18n'
import { processStepContentElementText } from '@flows-ui/utils/processStepContentElementText'
import type { Choice as ChoiceProps } from '@flows/core/types/stepContentTypes'
import type { FlowValues } from '@flows/core/types/stepTypes'
import { RadioSet } from '@ui-kit/components/formComponents/RadioSet'
import type { RadioOption, RadioSetType } from '@ui-kit/components/formComponents/RadioSet/types'

const radioSetType: Record<'button' | 'chip', RadioSetType> = {
  chip: 'radioChip',
  button: 'radioButton',
}

export const Choice = <
  TProps extends Omit<ChoiceProps<any, any>, 'type'> & { type: 'choice' | 'yesNo' },
>(
  props: TProps & { flowValues: FlowValues }
) => {
  const { t } = useTranslation()
  const servicesResponses = useFlowsServicesResponses()
  const translateFunction = processStepContentElementText(t, {
    flowValues: props.flowValues,
    servicesResponses,
  })

  const { control } = useFormContext()
  const { field, fieldState } = useController({
    control,
    name: props.name,
  })

  const options = useMemo(() => {
    return match(props.options)
      .with(P.string, (opts) => {
        return _get(props.flowValues, opts) as RadioOption[]
      })
      .otherwise((opts) => {
        return opts.map((option) => {
          const label = match(option.label)
            .with(P.union(P.boolean, P.number, P.nullish), () => option.value.toString())
            .otherwise((l) => translateFunction(l))

          const baseOptions = {
            value: option.value,
            label,
          }

          return match(option.subtitle)
            .with(P.not(P.nullish), (textContent) => ({
              ...baseOptions,
              subtitle: translateFunction(textContent),
            }))
            .otherwise(() => baseOptions)
        })
      })
  }, [props.flowValues, props.options, translateFunction])

  return (
    <RadioSet
      name={props.name}
      id={props.name}
      radioSetType={props.as && radioSetType[props.as] ? radioSetType[props.as] : 'radioBox'}
      layout={props.layout}
      onValueChange={(newValue) => {
        field.onChange(newValue)
      }}
      radioOptions={options}
      disabled={props.disabled}
      value={field.value}
      error={fieldState.error?.message}
    />
  )
}
